/* ---------------------------------------------------------------------------
   The construction record, played back on the model — Construction Monitoring's
   "Simulate" on the record's 3D canvas. Construction Scheduler's Simulate is the
   same player on the accepted programme instead (SimInput.programme): the parts
   as planned, the card listing what the baseline has under way.

   WHAT IT SHOWS IS WHAT WAS RECORDED. src/data/buildSequence.ts lays the
   structure's works entries onto the parts of the imported model; this module
   measures the parts for it, then steps through the days drawing each part in
   one of four ways:

     · not yet built — a faint ghost, so the work still to do stays in view;
     · under way — amber and translucent (bored but not concreted, shuttered
       but not poured);
     · finished today — green, fading in over the day;
     · built — the part's own material, as the model draws it.

   And round the parts, what the site stood up or dug that the model has no part
   for — the excavations, their cofferdams, the formwork (./siteWorks3d.ts).

   Under the canvas, a bar: play, step, a slider over every calendar day of the
   record, and what the site recorded that day — the entries, and the weather off
   the daily return, so an idle day reads as an idle day and not as a gap.

   IT CHANGES NOTHING BUT MATERIALS, and puts every one back. The model's own
   materials are shared between parts (one per colour, see functions/lib/meshGlb.ts),
   so a part is recoloured by swapping its material for one of three this module
   owns, never by editing the shared one — and closing restores the originals and
   disposes the three. Nothing is written anywhere.

   THREE.JS IS PASSED IN, as everywhere in src/modeler: it is an on-demand chunk.
   --------------------------------------------------------------------------- */

import { icon, type IconPath } from '../core/dom.ts';
import { activityLabel } from '../data/activities.ts';
import { nodeId, nodeLabel } from '../data/componentTree.ts';
import { firstShown, planBuild, planProgramme, stateOn, buildsPart, type BuildPlan, type PartInfo, type ProgrammePlan } from '../data/buildSequence.ts';
import { weatherLabel, weatherObstructs } from '../data/siteReturn.ts';
import { mountSiteWorks } from './siteWorks3d.ts';
import { weatherGlyph, weatherKey, weatherSky } from '../components/weather.ts';
import type { ProgressEntry, SiteReturn } from '../data/types.ts';
import type { Baseline, BaselineActivity } from '../data/variance.ts';

type Three = typeof import('three');
type Mesh = import('three').Mesh;
type Material = import('three').Material | import('three').Material[];

export interface SimInput {
  entries: readonly ProgressEntry[];
  returns: readonly SiteReturn[];
  /**
   * Where given, the programme is played instead of the record — Construction
   * Scheduler's Simulate: every part as the accepted baseline plans it, before
   * anybody has built it. See planProgramme in src/data/buildSequence.ts.
   */
  programme?: Baseline | null;
}

export interface BuildSim {
  /** Advance the playback by a frame's time, in seconds. Called from the render loop. */
  frame(dt: number): void;
  /** The record changed underneath — replan, keeping the day on screen. */
  setInput(input: SimInput): void;
  setTheme(theme: string): void;
  /** Put every part back as it was and take the bar down. */
  close(): void;
}

/** Days of record per second at 1×. Seventy days play in under a minute. */
const DAYS_PER_SECOND = 1.4;
const SPEEDS = [1, 2, 4, 8] as const;
/** Entries that change the site round the model rather than a part of it — listed as loud as a pour. */
const EARTHWORKS: ReadonlySet<string> = new Set(['excavation', 'backfill']);

/** How the three states are drawn, per ground. */
const LOOK = {
  light: { ghost: 0x8b95a3, ghostOpacity: 0.1, progress: 0xd08a1e, fresh: 0x2e9d5f },
  dark: { ghost: 0x9aa4b4, ghostOpacity: 0.09, progress: 0xe0a33f, fresh: 0x4cc07e },
};

const ICON_PLAY: IconPath = 'M7 4.5v15l12-7.5z';
const ICON_PAUSE: IconPath = ['M8 5v14', 'M16 5v14'];
const ICON_RESTART: IconPath = ['M5 5v14', 'M19 5 9 12l10 7z'];
const ICON_BACK: IconPath = ['M15 6l-6 6 6 6'];
const ICON_FORWARD: IconPath = ['M9 6l6 6-6 6'];
const ICON_CLOSE: IconPath = ['M6 6l12 12', 'M18 6 6 18'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function entryLine(e: ProgressEntry): string {
  const what = activityLabel(e.activity);
  const where = e.component ? ` · ${e.component}` : '';
  const much = e.quantity !== null && e.unit ? ` · ${e.quantity} ${e.unit}` : '';
  return `${what}${where}${much}`;
}

/** A planned activity, with the group it is in: "Bored piles · Abutment A1". */
function plannedLine(a: BaselineActivity, byId: ReadonlyMap<string, BaselineActivity>): string {
  const group = a.parentId ? byId.get(a.parentId) : undefined;
  return group ? `${a.name} · ${group.name}` : a.name;
}

function button(title: string, path: IconPath, press: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'mtool';
  b.title = title;
  b.setAttribute('aria-label', title);
  b.appendChild(icon(path, { size: 16, width: 1.8 }));
  b.addEventListener('click', press);
  return b;
}

/**
 * Measure every part of the model along and across the bridge.
 *
 * The long axis is whichever horizontal extent is longer — the rule
 * `axialFrame` in ./meshTools.ts uses, so "along" means the same thing to the
 * playback as to the Front view.
 */
export function measure(THREE: Three, root: import('three').Object3D): { meshes: Mesh[]; parts: PartInfo[] } {
  root.updateMatrixWorld(true);
  const all = new THREE.Box3().setFromObject(root);
  const size = all.getSize(new THREE.Vector3());
  const alongX = size.x >= size.z;
  const meshes: Mesh[] = [];
  const parts: PartInfo[] = [];
  const box = new THREE.Box3();
  const c = new THREE.Vector3();
  root.traverse((node) => {
    const mesh = node as Mesh;
    if (!mesh.isMesh) return;
    box.setFromObject(mesh);
    if (box.isEmpty()) return;
    box.getCenter(c);
    meshes.push(mesh);
    parts.push({
      sourceType: String(mesh.userData?.['sourceType'] ?? mesh.parent?.userData?.['label'] ?? ''),
      label: nodeLabel(mesh),
      id: nodeId(mesh),
      station: alongX ? c.x : c.z,
      min: alongX ? box.min.x : box.min.z,
      max: alongX ? box.max.x : box.max.z,
      lateral: alongX ? c.z : c.x,
    });
  });
  return { meshes, parts };
}

/**
 * Start the playback on a model that is on screen. `host` is the canvas's host,
 * which the bar is added to; `onClose` is told when the bar's own close is pressed.
 */
export function startBuildSim(
  THREE: Three,
  root: import('three').Object3D,
  host: HTMLElement,
  input: SimInput,
  theme: string,
  onClose: () => void,
): BuildSim {
  const { meshes, parts } = measure(THREE, root);
  const original = new Map<Mesh, Material>(meshes.map((m) => [m, m.material as Material]));

  /* FLAT-SHADED AND DOUBLE-SIDED, as the model's own are. The derived .glb carries
     no normals (functions/lib/meshGlb.ts), so GLTFLoader draws it flat; a smooth
     material on the same triangles is lit from normals that are not there, and
     every part it touches comes out black. */
  const base = { side: THREE.DoubleSide, flatShading: true, metalness: 0, roughness: 0.85 } as const;
  const ghost = new THREE.MeshStandardMaterial({ ...base, transparent: true, depthWrite: false });
  const progress = new THREE.MeshStandardMaterial({ ...base, transparent: true, opacity: 0.8 });
  const fresh = new THREE.MeshStandardMaterial({ ...base, transparent: true });
  const paint = (next: string): void => {
    const look = next === 'dark' ? LOOK.dark : LOOK.light;
    ghost.color.setHex(look.ghost);
    ghost.opacity = look.ghostOpacity;
    progress.color.setHex(look.progress);
    fresh.color.setHex(look.fresh);
    fresh.emissive.setHex(look.fresh);
  };
  paint(theme);
  const works = mountSiteWorks(THREE, root, meshes, parts, theme);

  let entries = input.entries;
  let returns = input.returns;
  let programme = input.programme ?? null;
  const planOf = (): BuildPlan | ProgrammePlan =>
    programme ? planProgramme(parts, programme.activities) : planBuild(parts, entries);
  let plan = planOf();
  /* Opened on the first day anything shows on the model, so pressing play builds
     the bridge from nothing — not on weeks of mobilisation and detour drawn as
     an empty model. Those days are off the slider too. */
  let from = firstShown(plan);
  let t = from;
  let playing = false;
  /* A programme runs to months, a record to weeks: the programme starts faster. */
  let speed: (typeof SPEEDS)[number] = programme ? 4 : 1;
  let shownDay = -1;

  /* --------------------------- the bar --------------------------- */

  const bar = document.createElement('div');
  bar.className = 'msim';
  bar.setAttribute('role', 'region');
  bar.setAttribute('aria-label', programme ? 'Construction simulation' : 'Construction playback');

  const card = document.createElement('div');
  card.className = 'msim__card';
  const dateEl = document.createElement('div');
  dateEl.className = 'msim__date';
  const weatherEl = document.createElement('div');
  weatherEl.className = 'msim__weather';
  const listEl = document.createElement('ul');
  listEl.className = 'msim__list';
  card.append(dateEl, weatherEl, listEl);
  /* The day's sky is the card's background, moving as the days play past. */
  const sky = weatherSky(true);
  const setSky = (weather: string | null): void => {
    if (weather === null) {
      delete card.dataset.wx;
      sky.remove();
    } else {
      card.dataset.wx = weatherKey(weather);
      if (!sky.isConnected) card.prepend(sky);
    }
  };

  const legend = document.createElement('div');
  legend.className = 'msim__legend';
  const count = (cls: string, text: string): HTMLSpanElement => {
    const item = document.createElement('span');
    item.className = 'msim__key';
    const swatch = document.createElement('i');
    swatch.className = `msim__swatch msim__swatch--${cls}`;
    const label = document.createElement('span');
    label.textContent = text;
    item.append(swatch, label);
    legend.appendChild(item);
    return label;
  };
  const builtEl = count('built', '');
  const todayEl = count('today', '');
  const underEl = count('progress', '');
  const toDoEl = count('ghost', '');
  const pitEl = count('pit', '');
  const formEl = count('form', '');

  const controls = document.createElement('div');
  controls.className = 'msim__controls';
  const restart = button('Back to the first day', ICON_RESTART, () => seek(from));
  const back = button('Previous day', ICON_BACK, () => seek(Math.floor(t) - 1));
  const play = button('Play', ICON_PLAY, () => setPlaying(!playing));
  const forward = button('Next day', ICON_FORWARD, () => seek(Math.floor(t) + 1));
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'msim__slider';
  slider.min = '0';
  slider.step = '1';
  slider.setAttribute('aria-label', 'Day of the record');
  slider.addEventListener('input', () => seek(Number(slider.value)));
  const dayEl = document.createElement('span');
  dayEl.className = 'msim__day';
  const speedBtn = document.createElement('button');
  speedBtn.type = 'button';
  speedBtn.className = 'mtools__view msim__speed';
  speedBtn.title = 'Playback speed';
  speedBtn.addEventListener('click', () => {
    speed = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]!;
    speedBtn.textContent = `${speed}×`;
  });
  speedBtn.textContent = `${speed}×`;
  const close = button('Close the playback', ICON_CLOSE, () => onClose());
  controls.append(restart, back, play, forward, slider, dayEl, speedBtn, close);

  bar.append(card, legend, controls);
  host.appendChild(bar);
  host.classList.add('det-live--simulating');

  /* --------------------------- drawing a day --------------------------- */

  const last = (): number => Math.max(0, plan.days.length - 1);

  const setPlaying = (on: boolean): void => {
    /* Play at the end starts again from the beginning, as any player does. */
    if (on && Math.floor(t) >= last()) t = from;
    playing = on && plan.days.length > 0;
    play.replaceChildren(icon(playing ? ICON_PAUSE : ICON_PLAY, { size: 16, width: 1.8 }));
    play.title = playing ? 'Pause' : 'Play';
    play.setAttribute('aria-label', play.title);
    play.classList.toggle('is-active', playing);
  };

  const seek = (day: number): void => {
    t = Math.max(from, Math.min(last(), day));
    setPlaying(false);
    showDay(Math.floor(t), true);
  };

  const showDay = (day: number, force = false): void => {
    if (day === shownDay && !force) return;
    shownDay = day;

    let built = 0;
    let today = 0;
    let under = 0;
    let toDo = 0;
    /* The programme's last day is the bridge as planned, finished: nothing on it is
       news. The record's last day is today, where what finished is. */
    const complete = programme !== null && day >= last();
    meshes.forEach((mesh, i) => {
      const state = plan.days.length ? stateOn(plan, i, day) : 'none';
      if (state === 'done' && plan.done[i] === day && !complete) {
        mesh.material = fresh;
        today += 1;
      } else if (state === 'done') {
        mesh.material = original.get(mesh)!;
        built += 1;
      } else if (state === 'progress') {
        mesh.material = progress;
        under += 1;
      } else {
        mesh.material = ghost;
        toDo += 1;
      }
    });
    builtEl.textContent = `Built ${built}`;
    todayEl.textContent = `Finished today ${today}`;
    underEl.textContent = `Under way ${under}`;
    toDoEl.textContent = `Not started ${toDo}`;

    const site = works.show(plan.pits, day, 'forms' in plan ? (plan.forms[day] ?? []) : []);
    pitEl.textContent = `Excavation open ${site.pits}`;
    formEl.textContent = `In formwork ${site.forms}`;
    pitEl.parentElement!.hidden = plan.pits.length === 0;
    formEl.parentElement!.hidden = !('forms' in plan) || plan.forms.every((f) => f.length === 0);

    slider.min = String(from);
    slider.max = String(last());
    slider.value = String(day);
    back.disabled = day <= from;
    forward.disabled = day >= last();

    const iso = plan.days[day];
    if (!iso) {
      dateEl.textContent = 'Nothing to play';
      setSky(null);
      weatherEl.textContent = '';
      listEl.replaceChildren(
        Object.assign(document.createElement('li'), {
          textContent: programme ? 'Nothing in the programme builds or digs at a part of this model.' : 'No work has been recorded against this structure.',
        }),
      );
      dayEl.textContent = '';
      return;
    }
    dateEl.textContent = dayLabel(iso);
    dayEl.textContent = `Day ${day - from + 1} of ${plan.days.length - from}`;

    if ('activeByDay' in plan) {
      showPlanned(plan, day);
      return;
    }

    /* The day's sky, moving as the days play past; plain words where the day
       has no return. */
    const ret = returns.find((r) => r.returnedOn === iso);
    if (ret) {
      setSky(ret.weather);
      weatherEl.replaceChildren(
        weatherGlyph(ret.weather, 15),
        Object.assign(document.createElement('span'), { className: 'wxchip__label', textContent: weatherLabel(ret.weather) }),
        ...(ret.labour === null
          ? []
          : [Object.assign(document.createElement('span'), { className: 'wxchip__detail', textContent: `${ret.labour} on site` })]),
        ...(weatherObstructs(ret.weather)
          ? [Object.assign(document.createElement('span'), { className: 'wxchip__lost', textContent: 'lost' })]
          : []),
      );
    } else {
      setSky(null);
      weatherEl.textContent = 'No daily return';
    }

    const todays = plan.entriesByDay[day] ?? [];
    const lines = todays.slice(0, 4).map((e) => {
      const li = document.createElement('li');
      li.textContent = entryLine(e);
      /* The entries that change nothing on the model — a survey, a hold point, a
         stoppage — are listed quieter, so the eye goes to what was built. */
      if (!buildsPart(e.activity) && !EARTHWORKS.has(e.activity)) li.className = 'is-quiet';
      li.title = e.note;
      return li;
    });
    if (todays.length > 4) {
      lines.push(Object.assign(document.createElement('li'), { textContent: `and ${todays.length - 4} more`, className: 'is-quiet' }));
    }
    if (lines.length === 0) {
      lines.push(Object.assign(document.createElement('li'), { textContent: 'No work recorded', className: 'is-quiet' }));
    }
    listEl.replaceChildren(...lines);
  };

  /* The programme's day: what the baseline has under way, and no sky — nobody
     knows the weather of a day that has not come. */
  const showPlanned = (p: ProgrammePlan, day: number): void => {
    setSky(null);
    weatherEl.textContent = programme ? `As planned · ${programme.name}` : 'As planned';
    const byId = new Map((programme?.activities ?? []).map((a) => [a.activityId, a]));
    const todays = p.activeByDay[day] ?? [];
    /* Only what builds a part or digs a pit: planProgramme leaves out the rest. */
    const lines = todays.slice(0, 4).map((a) => {
      const li = document.createElement('li');
      li.textContent = plannedLine(a, byId);
      li.title = `${a.activityId} · ${a.plannedStart} to ${a.plannedFinish}`;
      return li;
    });
    if (todays.length > 4) {
      lines.push(Object.assign(document.createElement('li'), { textContent: `and ${todays.length - 4} more`, className: 'is-quiet' }));
    }
    if (lines.length === 0) {
      lines.push(Object.assign(document.createElement('li'), { textContent: 'Nothing planned on the model', className: 'is-quiet' }));
    }
    listEl.replaceChildren(...lines);
  };

  setPlaying(false);
  showDay(from, true);

  return {
    frame(dt) {
      if (playing) {
        t += Math.min(dt, 0.1) * DAYS_PER_SECOND * speed;
        if (t >= last()) {
          t = last();
          setPlaying(false);
        }
        showDay(Math.floor(t));
      }
      /* What finished today fades in across the day — whole when paused on it. */
      const within = playing ? t - Math.floor(t) : 1;
      const k = Math.min(1, within / 0.6);
      fresh.opacity = 0.25 + 0.75 * k;
      fresh.emissiveIntensity = 0.45 * (1 - k) + 0.12;
    },
    setInput(next) {
      const nextProgramme = next.programme ?? null;
      if (next.entries === entries && next.returns === returns && nextProgramme === programme) return;
      const onDay = plan.days[Math.floor(t)];
      entries = next.entries;
      returns = next.returns;
      programme = nextProgramme;
      plan = planOf();
      from = firstShown(plan);
      const again = onDay ? plan.days.indexOf(onDay) : -1;
      t = Math.max(from, again >= 0 ? again : Math.min(t, last()));
      showDay(Math.floor(t), true);
    },
    setTheme(next) {
      paint(next);
      works.setTheme(next);
    },
    close() {
      for (const [mesh, material] of original) mesh.material = material;
      ghost.dispose();
      progress.dispose();
      fresh.dispose();
      works.dispose();
      bar.remove();
      host.classList.remove('det-live--simulating');
    },
  };
}
